import { Component, Input, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { sessionStorageKey } from 'src/app/doctors-station/doctors-station.constants';
import { DxauthService } from 'src/app/dxauth.service';
import { environment } from 'src/environments/environment';
import { Auth0Service } from '../../services/auth0.service';
import { Auth0SignupResult } from '../../services/auth0.types';
import { errorDescriptionsMap, getOptionsFromRedirectUri } from '../../utils';
import { FaviconSwitchComponent } from '../base-reset-password/favicon-switch.component';
import { dates, months } from './const';

export class BaseLoginComponent_ extends FaviconSwitchComponent {
  // oidc用
  clientId: string;
  redirectUri: string;
  state: string;
  responseType: string;
  nonce: string;
  scope: string;
  responseMode: string;
  codeChallenge?: string;
  codeChallengeMethod?: 'S256';
  // id基盤用
  role: string;

  // ログイン用
  protected email = '';
  protected password = '';

  // 新規登録用
  protected firstName = '';
  protected lastName = '';
  // 登録画面では不要ということでundefined
  public gender?: 'male' | 'female';
  public birthDate?: string;
  protected query: Params;
  public initialized = false;
  public acceptTerm = false;
  public confirmPassword = '';
  public isSaveEmail = false;

  public masterData: Record<
    string,
    {
      label: string;
      value?: unknown;
    }[]
  > = {
    years: years().map((e) => ({
      label: `${e}年`,
      value: e,
    })),
    month: months,
    dates: dates,
  };

  errorMessages = {
    login: {
      email: '',
      password: '',
    },
    signup: {
      givenName: '',
      familyName: '',
      email: '',
      password: '',
      confirmPassword: '',
      gender: '',
      birthDate: '',
      birthDateYear: '',
      birthDateMonth: '',
      birthDateDate: '',
      terms: '',
      privacyPolicy: '',
      zipcode: '',
      city: '',
      address: '',
      building: '',
      town: '',
    },
    forgetpassword: {
      email: '',
    },
  };

  passwordToggle = {
    password: false,
    confirm: false,
  };

  constructor(
    activateRoute: ActivatedRoute,
    protected auth: Auth0Service,
    protected dxauth: DxauthService,
    protected spinner: NgxSpinnerService,
    protected router: Router
  ) {
    super();
    activateRoute.queryParams.subscribe((query) => {
      this.query = query;
      this.responseType = query['response_type'];
      this.clientId = query['client_id'];
      this.scope = query['scope'];
      this.state = query['state'];
      this.nonce = query['nonce'];
      this.redirectUri = query['redirect_uri'];
      this.codeChallenge = query['code_challenge'];
      this.codeChallengeMethod = query['code_challenge_method'];
      this.role = this.parseRole(this.redirectUri);
      this.init();
    });
  }

  setDefaultEmailFromQuery() {
    const parsed = getOptionsFromRedirectUri(this.redirectUri);
    if (!parsed.email) return;
    this.email = parsed.email;
  }

  loadEmailFromStorage() {
    const loaded = this.loadEmailAddress();
    if (loaded) {
      this.email = loaded.email;
      this.isSaveEmail = true;
      return true;
    }
    return false;
  }

  clearEmailAddress() {
    localStorage.removeItem(btoa(this.clientId));
  }
  saveEmailAddress() {
    localStorage.setItem(
      btoa(this.clientId),
      JSON.stringify({ email: btoa(this.email) }) // base64
    );
  }
  loadEmailAddress() {
    const str = localStorage.getItem(btoa(this.clientId));
    if (!str) return undefined;
    const loaded: { email: string } = JSON.parse(str);
    loaded.email = atob(loaded.email); // 復号
    return loaded;
  }

  /** 新規登録時に付与するロール */
  protected registrationRole(): string | undefined {
    return undefined;
  }

  parseRole(redirectUri: string) {
    if (!redirectUri) return 'patient';
    const match = redirectUri.match(/role=(\w+)/);
    if (!match) return 'patient';
    return match[1];
  }

  async init() {
    // ログイン済みかチェック
    this.autologin();
  }

  /** ログイン方法がLINE SSOであるか否かを返す */
  private get isLineLogin() {
    return this.app?.connection === 'LINE';
  }

  async autologin() {
    // 設定で非表示になっている場合はローディングの表示を行わない
    if (!this.app?.spinner?.autologin?.isHide) {
      this.spinner.show();
    }

    // LINEログインを行うアプリケーションの場合は、セッションの状況に関わらずLINEログインを行う
    if (this.isLineLogin) {
      this.auth.loginByLineSSO();
      return;
    }
    try {
      const isLogin = await this.auth.autoLoginWithSession({
        clientId: this.clientId,
        nonce: this.nonce,
        redirectUri: this.redirectUri,
        responseMode: this.responseMode,
        responseType: this.responseType,
        scope: this.scope,
        state: this.state,
        codeChallenge: this.codeChallenge,
        codeChallengeMethod: this.codeChallengeMethod,
      });
      // ログイン済みは何もしない
      if (isLogin) {
        return;
      }
      setTimeout(() => {
        this.initialized = true;
      }, 500);
    } catch (error) {}
    this.spinner.hide();
  }

  isLoginInfoInput() {
    this.errorMessages.login.password = this.password
      ? ''
      : 'パスワードを入力してください';
    this.errorMessages.login.email = this.email
      ? ''
      : 'メールアドレスを入力してください。';
    if (!this.password || !this.email) {
      return false;
    }

    return true;
  }

  loginAndResendInviteEmail() {
    // フラグを立てて、ログイン処理をする
    sessionStorage.setItem(
      sessionStorageKey.loginAndResendInviteEmail,
      this.email
    );
    return this.login();
  }
  login(params?: { clientId: string; redirectUri: string }) {
    if (!this.isLoginInfoInput()) return;
    // 仮ログイン時に
    if (params) {
      // 特殊ルート. 現状CPのconnect向けのみ. この場合はurlのクエリ部をそのままではなく、connect用のclient_idとredirect_uriを保存する
      sessionStorage.setItem(
        environment.sessionStorageKey,
        JSON.stringify({
          ...this.query,
          client_id: params.clientId,
          redirect_uri: params.redirectUri,
        })
      );
    } else {
      // 通常ルート. この場合はurlのクエリ部をそのまま保存する
      sessionStorage.setItem(
        environment.sessionStorageKey,
        JSON.stringify(this.query)
      );
    }
    sessionStorage.setItem(sessionStorageKey.verifyEmail, this.email);
    sessionStorage.setItem(sessionStorageKey.verifyEmailReferer, 'login');

    this.spinner.show();
    // メールアドレスの保存
    if (this.isSaveEmail) {
      this.saveEmailAddress();
    } else {
      this.clearEmailAddress();
    }
    this.auth
      .loginFirst({
        clientId: params?.clientId || this.clientId,
        email: this.email,
        password: this.password,
        redirectUri: params?.redirectUri || this.redirectUri,
        responseType: this.responseType,
        codeChallenge: this.codeChallenge,
        codeChallengeMethod: this.codeChallengeMethod,
        connection: environment.apps[this.clientId].connection, // コネクション指定がある場合はこっち
        options: {
          errorDescripitionsMap: this.errorDescripitionsMap,
        },
      })
      .catch((e) => {
        this.errorMessages.login.password = e;
      })
      .finally(() => {
        this.spinner.hide();
      });
  }

  protected get errorDescripitionsMap(): typeof errorDescriptionsMap {
    return errorDescriptionsMap;
  }

  submittable() {
    this.errorMessages.signup = {} as any;
    if (!this.email) {
      this.errorMessages.signup.email = 'メールアドレスを入力してください';
    } else {
      if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(this.email)) {
      } else {
        // this.errorMessages.signup.email =
        //   '正しいメールアドレスを入力してください';
      }
    }
    if (!this.password) {
      this.errorMessages.signup.password = 'パスワードを入力してください';
    }
    if (!this.confirmPassword) {
      this.errorMessages.signup.confirmPassword =
        '確認用パスワードを入力してください。';
    } else {
      if (this.password !== this.confirmPassword) {
        this.errorMessages.signup.confirmPassword =
          'パスワードが一致しません。';
      }
    }
    if (!this.lastName) {
      this.errorMessages.signup.familyName = '姓を入力してください。';
    } else {
      if (this.lastName.length > 20) {
        this.errorMessages.signup.familyName =
          '姓は20文字以内で入力してください。';
      }
    }
    if (!this.firstName) {
      this.errorMessages.signup.givenName = '名を入力してください。';
    } else {
      if (this.firstName.length > 20) {
        this.errorMessages.signup.givenName =
          '姓は20文字以内で入力してください。';
      }
    }

    if (!this.acceptTerm) {
      this.errorMessages.signup.terms =
        '利用規約およびプライバシーポリシーに同意してください';
    }

    return Object.keys(this.errorMessages.signup).length === 0;
  }

  /** 新規登録リクエスト */
  async register(params?: {
    profile?: string;
    afterRegisterHook?: (e: Auth0SignupResult) => Promise<void>;
  }) {
    if (!this.submittable()) {
      return;
    }
    const isIntegrationTarget = Auth0Service.isAccountIntegrationTarget({
      clientId: this.clientId,
    });

    // アカウント連携対象のclient_idの場合
    if (isIntegrationTarget) {
      // https://doctors-jp.backlog.com/view/DXUT-63
      // 既に登録済みの場合は、ログイン案内ページに遷移する
      if (await this.dxauth.isEmailExists({ email: this.email })) {
        sessionStorage.setItem(sessionStorageKey.loginGuideEmail, this.email);
        location.pathname = 'login-guide';
        return;
      }
    }

    this.spinner.show();

    try {
      const registerResult = await this.auth
        .register({
          clientId: this.clientId,
          email: this.email,
          password: this.password,
          firstName: this.firstName,
          lastName: this.lastName,
          birthDate: this.birthDate,
          gender: this.gender,
          defaultRole: this.registrationRole(),
          profile: params?.profile,
          connection: this.app?.connection,
        })
        .then(async (e) => {
          // メール送信完了画面向けにセッションに保存
          sessionStorage.setItem(sessionStorageKey.verifyEmail, this.email);
          sessionStorage.setItem(
            sessionStorageKey.verifyEmailReferer,
            'register'
          );
          if (params?.afterRegisterHook) {
            await params.afterRegisterHook(e);
          }
          return e;
        });

      sessionStorage.setItem(
        environment.sessionStorageKey,
        JSON.stringify(this.query)
      );

      //
      await this.auth.loginFirst({
        clientId: this.clientId,
        email: this.email,
        password: this.password,
        redirectUri: this.redirectUri,
        responseType: this.responseType,
        codeChallenge: this.codeChallenge,
        codeChallengeMethod: this.codeChallengeMethod,
        connection: this.app?.connection,
        options: {
          errorDescripitionsMap: this.errorDescripitionsMap,
        },
      });
      return registerResult;
    } catch (e) {
      if (e.code?.includes('error in email')) {
        this.errorMessages.signup.email =
          'メールアドレスが正しい形式ではありません';
      } else if (e?.name === 'PasswordStrengthError') {
        // パスワードが弱い場合
        this.errorMessages.signup.confirmPassword = `半角英数字と記号を3種類以上含めた、8文字以上のパスワードを入力してください。`;
      } else {
        // それ以外のエラー. Auth0のポリシーで、詳細なエラー内容は出さないようにされているため、汎用的なメッセージとする
        this.errorMessages.signup.email =
          '新規登録に失敗しました。Eメールアドレスが既に登録されていないか確認してください';
      }
    } finally {
      this.spinner.hide();
    }
  }

  async changePassword() {
    this.spinner.show();
    try {
      // アプリのタイプ種別が'lasuite'の場合は、パスワードリセットメールを送信する
      // 登録支援システム（type: 'cportal' かつ connection: 'CBPT-DxSales'）もパスワードリセットメールを送信する
      // 20230523暫定対応
      if (this.app && this.app.type) {
        if (
          this.app.type === 'lasuite' ||
          this.app.connection === 'CBPT-DxSales' ||
          this.app.connection === 'CBPT-MR' ||
          this.app.connection === 'DST-SOKUSAI-OEM' || // https://doctors-jp.backlog.com/view/DXUT-268
          this.app.connection === 'DST-HEALTHPICKS'
        ) {
          await this.auth.sendPasswordReset({
            clientId: this.clientId,
            email: this.email,
            connection: this.app?.connection,
          });
          this.dxauth
            .changePassword({
              email: this.email,
              connection: this.app?.connection,
            })
            .finally(() => {
              // 補助的な処理のため、完了は同期的に待つ必要はない
            });
          return;
        }
      }

      const isLogined = await this.auth.isLogined({
        clientId: this.clientId,
        redirectUri: this.redirectUri,
        email: this.email,
      });

      if (isLogined) {
        await this.auth.sendPasswordReset({
          clientId: this.clientId,
          email: this.email,
          connection: this.app?.connection,
        });
        this.dxauth
          .changePassword({
            email: this.email,
            connection: this.app?.connection,
          })
          .finally(() => {
            // 補助的な処理のため、完了は同期的に待つ必要はない
          });
      }
    } catch (error) {
      // todo表示
      throw error;
    } finally {
      this.spinner.hide();
    }
  }
}

function years() {
  const now = new Date().getFullYear();
  const length = 130;
  return Array.from({ length }).map((_, i) => now - length + i + 1);
}
export type PageType =
  | 'login'
  | 'register'
  | 'forgot-password'
  | 'password-reset-send'
  | 'register-resend';

@Component({
  selector: 'app-base-login',
  templateUrl: './base-login.component.html',
  styleUrls: ['./base-login.component.css'],
})
export class BaseLoginComponent extends BaseLoginComponent_ implements OnInit {
  @Input()
  clientId: string;

  email = '';
  password = '';
  firstName = '';
  lastName = '';

  inputState: Record<
    string,
    {
      isFocus: boolean;
    }
  > = {
    email: { isFocus: false },
    password: { isFocus: false },
    confirmPassword: { isFocus: false },
    firstName: { isFocus: false },
    lastName: { isFocus: false },
  };
  passwordState: 'text' | 'password' = 'password';

  page: PageType = 'login';

  registrationField = {
    years: years(),
    month: Array.from({ length: 12 }).map((e, i) => i + 1),
    days: Array.from({ length: 31 }).map((e, i) => i + 1),
    selected: {
      year: '2000',
      month: 1,
      day: 1,
    },
  };

  private _titleMaps: Record<PageType, string>;

  protected titleMaps(): Record<PageType, string> {
    return {
      'forgot-password': '',
      'password-reset-send': '',
      login: '',
      register: '',
      'register-resend': '',
    };
  }
  protected setEmail(email: string) {
    this.email = email;
  }
  constructor(
    protected activateRoute: ActivatedRoute,
    auth0: Auth0Service,
    dxauth: DxauthService,
    protected spinner: NgxSpinnerService,
    public router: Router,
    public title: Title
  ) {
    super(activateRoute, auth0, dxauth, spinner, router);
    this.updateBirthdate();

    this._titleMaps = this.titleMaps();
    // 新規登録ページに飛ばす
    activateRoute.fragment.subscribe((fragment) => {
      if (fragment === 'register') {
        this.navigate('register', true);
      }
    });
    activateRoute.queryParams.subscribe((queryParams) => {
      if (queryParams.error) {
        this.errorMessages.login.password = queryParams.error;
      }
    });

    this.router.routeReuseStrategy.shouldReuseRoute = (e) => {
      return true;
    };
    activateRoute.data.subscribe((data) => {
      // authorizeで来たものはloginに飛ばす
      if (!data.page && activateRoute.snapshot.fragment !== 'register') {
        this.navigate('login', true); // 戻るボタン対策. historyには含めない
        return;
      }
      this.page = (data.page as PageType) || 'login';
      if (this._titleMaps[this.page]) {
        this.title.setTitle(`${this._titleMaps[this.page]}`);
      }
      // ページ遷移時に引き継がない
      this.email = '';
      this.password = '';
      this.firstName = '';
      this.lastName = '';

      // メール保存されてたらログインページで読み込み
      if (this.page === 'login') {
        this.loadEmailFromStorage();
      }
    });
  }

  async ngOnInit() {}

  focus(val: { isFocus: boolean }) {
    val.isFocus = true;
  }
  blur(val: { isFocus: boolean }) {
    val.isFocus = false;
  }

  get inputLabelClass() {
    if (this.email.length || this.inputState.email.isFocus)
      return 'v-label theme--light v-label--active primary--text';
    return 'v-label theme--light';
  }
  get passwordLabelClass() {
    if (this.password.length || this.inputState.password.isFocus)
      return 'v-label theme--light v-label--active primary--text';
    return 'v-label theme--light';
  }

  get confirmPasswordLabelClass() {
    if (this.confirmPassword.length || this.inputState.confirmPassword.isFocus)
      return 'v-label theme--light v-label--active primary--text';
    return 'v-label theme--light';
  }

  get givennameLabelClass() {
    if (this.firstName.length || this.inputState.firstName.isFocus)
      return 'v-label theme--light v-label--active primary--text';
    return 'v-label theme--light';
  }

  get familyNameLabelClass() {
    if (this.lastName.length || this.inputState.lastName.isFocus)
      return 'v-label theme--light v-label--active primary--text';
    return 'v-label theme--light';
  }

  getLabelClass(key: string, value?: string) {
    if (!this.inputState[key]) {
      this.inputState[key] = { isFocus: false };
    }
    if (value?.length || this.inputState[key].isFocus)
      return 'v-label theme--light v-label--active primary--text';
    return 'v-label theme--light';
  }

  updateBirthdate() {
    // this.birthDate = this.birthDateStr;
  }

  get birthDateStr() {
    const newLocal = this.registrationField.selected;
    return `${newLocal.year}-${newLocal.month
      .toString()
      .padStart(2, '0')}-${newLocal.day.toString().padStart(2, '0')}`;
  }

  get checkedStyle() {
    if (!this.acceptTerm) {
      return {
        i: `v-icon notranslate mdi mdi-checkbox-blank-outline theme--light`,
        div: `v-input--selection-controls__ripple`,
      };
    }
    return {
      i: `v-icon notranslate mdi mdi-checkbox-marked theme--light primary--text`,
      div: `v-input--selection-controls__ripple primary--text`,
    };
  }

  check() {
    this.acceptTerm = !this.acceptTerm;
  }

  checkIsEmailSave() {
    this.isSaveEmail = !this.isSaveEmail;
  }
  get isEmailSaveCheckedStyle() {
    if (!this.isSaveEmail) {
      return {
        i: `v-icon notranslate mdi mdi-checkbox-blank-outline theme--light`,
        div: `v-input--selection-controls__ripple`,
      };
    }
    return {
      i: `v-icon notranslate mdi mdi-checkbox-marked theme--light primary--text`,
      div: `v-input--selection-controls__ripple primary--text`,
    };
  }

  async changePassword(): Promise<void> {
    if (!this.email) {
      this.errorMessages.forgetpassword.email =
        'メールアドレスを入力してください。';
      return;
    } else {
      this.errorMessages.forgetpassword.email = undefined;
    }

    const result = await super.changePassword();
    this.navigate('password-reset-send');
    sessionStorage.setItem(sessionStorageKey.passwordResetEmail, this.email);
    return result;
  }

  navigate(page: PageType, replaceUrl = false) {
    this.router.navigate([page], {
      queryParamsHandling: 'preserve',
      replaceUrl,
    });
  }

  async isValidEmailAndPassword() {
    return this.auth.dxauth
      .isValidEmailAndPassword({
        clientId: this.clientId,
        email: this.email,
        password: this.password,
      })
      .catch((error) => {
        this.errorMessages.login.password = error;
        throw error;
      });
  }

  protected get errorDescripitionsMap(): typeof errorDescriptionsMap {
    return errorDescriptionsMap;
  }

  get termsUrl() {
    return environment.apps[this.clientId]?.termUrl;
  }

  get privacyPolicyUrl() {
    return environment.apps[this.clientId]?.privacyUrl;
  }
}
